import { defaultLineHeightFactor as fallbackLineheight, getAnchor } from "../text/shared";
import { PapyrusComponent, TextboxElement } from "../types";
import { spotColorDefinitionString } from "../util/color";
import Handlebars from "handlebars";

export const Textbox: PapyrusComponent<Omit<TextboxElement, "type">> = (props) => {
    const rotation = props.rotation || 0;
    const angleInRadians = (rotation * Math.PI) / 180.0;
    const rotC = Math.cos(angleInRadians);
    const rotS = -Math.sin(angleInRadians);
    const curvedPathId = `text-path-${props.id}`;
    const textFillId = `text-fill-${props.id}`;

    const xTranslation = props.x + (props.curved ? 0 : props.width / 2);
    const yTranslation = props.y + (props.curved ? 0 : props.height / 2);

    let text = "";
    try {
        text = Handlebars.compile(props.text || "")(props._renderingConfiguration?.templatingContext);
    } catch (e: any) {
        text = props.text ?? "";
    }

    const lines = text.split("\n");

    const stroke =
        props.strokeColor?.browserValue && props.strokeThickness ? props.strokeColor?.browserValue : undefined;
    const strokeWidth = props.strokeColor?.browserValue && props.strokeThickness ? props.strokeThickness : undefined;

    if (props.curved && !props.paths) {
        return null;
    }

    if (!props.fontData) {
        return null;
    }

    const getFill = (): string => {
        if (props.textFillImage) {
            return `url("#${textFillId}")`;
        }
        if (props._renderingConfiguration?.spotColors && props.fillSpotColorDefinition) {
            return `${props.fill} ${spotColorDefinitionString(props.fillSpotColorDefinition)}`;
        }
        return props.fill;
    };

    return (
        <>
            <defs>
                {props.textFillImage && (
                    <pattern
                        id={textFillId}
                        patternUnits="userSpaceOnUse"
                        width={props.textFillImage.scale * props.textFillImage.width}
                        height={props.textFillImage.scale * props.textFillImage.height}
                        x={0}
                        y={0}
                    >
                        <image
                            href={props.textFillImage.src}
                            xlinkHref={props.textFillImage.src}
                            width={props.textFillImage.scale * props.textFillImage.width}
                            height={props.textFillImage.scale * props.textFillImage.height}
                        />
                    </pattern>
                )}
                <style
                    type="text/css"
                    dangerouslySetInnerHTML={{
                        __html: `
                        @font-face {
                            font-family: '${props.fontData.name}';
                            src: url('${props.fontData.assetUrl}') format('truetype');
                        }
                        `,
                    }}
                />
                {props.curved && props.paths ? <path id={curvedPathId} d={props.paths[0]} /> : undefined}
            </defs>
            {props._renderingConfiguration?.debug ? (
                <rect stroke="blue" fill="none" x={props.x} y={props.y} width={props.width} height={props.height} />
            ) : undefined}

            <g mask={props.stepName ? undefined : props.mask}>
                <g transform={`matrix(${rotC}, ${-rotS}, ${rotS}, ${rotC}, ${xTranslation}, ${yTranslation})`}>
                    <text
                        xmlSpace="preserve"
                        fontFamily={`'${props.fontData.name}'`}
                        fontSize={props.fontSize}
                        fontStyle="normal"
                        fontWeight="normal"
                        fill={getFill()}
                        style={{
                            whiteSpace: "pre",
                            userSelect: "none",
                        }}
                        stroke={stroke}
                        strokeWidth={strokeWidth}
                    >
                        {props.curved ? (
                            <TextPath text={text} curvedPathId={curvedPathId} align={props.align} />
                        ) : (
                            lines.map((line, idx) => {
                                return (
                                    <TextSpan
                                        key={idx}
                                        align={props.vertical ? "center" : props.align}
                                        fontSize={props.fontSize}
                                        thisLineIdx={idx}
                                        amountLines={lines.length}
                                        text={line}
                                        textboxHeight={props.height}
                                        textboxWidth={props.width}
                                        lineHeight={props.lineHeight}
                                        vertical={props.vertical}
                                        verticalAlign={props.verticalAlign}
                                    />
                                );
                            })
                        )}
                    </text>
                </g>
            </g>
        </>
    );
};

const TextSpan: PapyrusComponent<{
    text: string;
    fontSize: number;
    thisLineIdx: number;
    amountLines: number;
    lineHeight?: number;
    textboxHeight: number;
    textboxWidth: number;
    align?: string;
    vertical?: boolean;
    verticalAlign?: string;
}> = (props) => {
    const getLineHeight = () => {
        if (props.lineHeight !== undefined) {
            return props.lineHeight;
        }
        return fallbackLineheight;
    };

    const getX = () => {
        if (props.align === "left") {
            return -props.textboxWidth / 2.0;
        }
        if (props.align === "right") {
            return props.textboxWidth / 2.0;
        }
        return 0.0;
    };

    // TODO: Write tests for this logic. Check coverage for lines
    const getY = () => {
        const lineHeight = props.fontSize * getLineHeight();
        const halfBoxHeight = props.textboxHeight / 2.0;

        if (props.verticalAlign === "top") {
            return -halfBoxHeight + (props.fontSize * 3) / 4 + props.thisLineIdx * lineHeight;
        }

        if (props.verticalAlign === "bottom") {
            const reverseIdx = props.amountLines - 1 - props.thisLineIdx;
            return halfBoxHeight - props.fontSize / 4 - reverseIdx * lineHeight;
        }

        // Vertically align to middle.
        // Lines before the middle line shift up,
        // lines after the middle line shift down.
        const middleLineIdx = (props.amountLines - 1) / 2.0;
        return (props.thisLineIdx - middleLineIdx) * lineHeight + props.fontSize / 4;
    };

    return (
        <tspan textAnchor={getAnchor(props.align)} x={getX()} y={`${getY()}px`}>
            {props.text}
        </tspan>
    );
};

const TextPath: PapyrusComponent<{
    text: string;
    curvedPathId: string;
    align?: string;
}> = (props) => {
    const targetId = `#${props.curvedPathId}`;
    const getStartOffset = () => {
        if (props.align === "left") {
            return "0%";
        }
        if (props.align === "right") {
            return "100%";
        }
        return "50%";
    };
    return (
        <textPath
            startOffset={getStartOffset()}
            textAnchor={getAnchor(props.align)}
            href={targetId}
            xlinkHref={targetId}
        >
            {props.text}
        </textPath>
    );
};
